import { z } from "zod"

const ErrorSchema = z.object({
  code: z.string(),
  message: z.string().optional(),
})

const JsonErrorResponseSchema = z.object({
  success: z.literal(false),
  errors: z.array(ErrorSchema).optional(),
  status: z.number(),
})

type ApiError = z.infer<typeof JsonErrorResponseSchema>

export type NormalizedError = {
  status: number
  data: NonNullable<ApiError["errors"]>
}

export const NormalizedErrorResult = {
  create: (status: number, errors?: NonNullable<ApiError["errors"]>): NormalizedError => ({
    status,
    data: errors || [],
  }),
  noResponse: (): NormalizedError => ({
    status: 500,
    data: [{ code: "no_response" }],
  }),
  noContentType: (): NormalizedError => ({
    status: 500,
    data: [{ code: "no_content_type" }],
  }),
  unsupportedContentType: (contentType: string): NormalizedError => ({
    status: 500,
    data: [{ code: "unsupported_content_type", message: `Unsupported content type: ${contentType}` }],
  }),
  parseError: (message = "An unknown error occurred while parsing response"): NormalizedError => ({
    status: 500,
    data: [{ code: "parse_error", message }],
  }),
  unknownError: (message = "An unknown error occured"): NormalizedError => ({
    status: 500,
    data: [{ code: "unknown_error", message }],
  }),
  unexpectedResponseShape: (): NormalizedError => ({
    status: 500,
    data: [{ code: "unexpected_response_shape", message: "Unexpected response shape from server" }],
  }),
}

export const normalizeApiErrorResponse = (data: unknown): NormalizedError => {
  const parsed = JsonErrorResponseSchema.safeParse(data)
  if (!parsed.success) return NormalizedErrorResult.unexpectedResponseShape()
  const result = NormalizedErrorResult.create(parsed.data.status, parsed.data.errors)
  return result
}
